import { Info } from "lucide-react";
import { compilePolicy, describeRule, policyCaveats, type ManagementPolicy } from "@app/shared/policy";

/**
 * What the chosen policy will actually do, derived by running the real compiler in the
 * browser.
 *
 * **This is not a second description of the presets.** It calls `compilePolicy` — the same
 * function the server calls on write — and renders `describeRule` over its output, so the
 * card structurally cannot promise something the server would not store. A hand-written list
 * of "what preset X does" would be a copy, and a copy is what goes stale the first time the
 * compile table changes. The test asserts this against `compilePolicy`, not against a literal.
 *
 * `policyCaveats` is rendered underneath and is the more important half: a card that only
 * lists what it does is how someone ends up believing a checkbox did something it did not.
 */
const NOTHING_MANAGED = "Nothing is managed after entry — the stop stays where the signal put it.";

export function RulesPreview({ policy }: { policy: ManagementPolicy }) {
  const rules = compilePolicy(policy);
  // `policyCaveats` says this too, in the one case that produces an empty rule set. Shown in
  // the rules area rather than among the caveats, because an empty box reads as "loading" or
  // "broken" — and filtered out below so the card does not say it twice.
  const caveats = policyCaveats(policy).filter((line) => rules.length > 0 || line !== NOTHING_MANAGED);

  return (
    <div className="space-y-3 rounded-md border border-border bg-muted/40 p-3">
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        What this does
      </div>

      {rules.length === 0 ? (
        <p className="text-sm">{NOTHING_MANAGED}</p>
      ) : (
        <ol className="space-y-1.5">
          {rules.map((rule, index) => (
            <li key={index} className="text-sm leading-snug">
              {describeRule(rule)}
            </li>
          ))}
        </ol>
      )}

      {caveats.length > 0 && (
        <ul className="space-y-1.5 border-t border-border pt-2">
          {caveats.map((line, index) => (
            <li key={index} className="flex gap-2 text-xs leading-snug text-muted-foreground">
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>{line}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
